import Component from '@glimmer/component';
import { trackedFunction } from 'reactiveweb/function';
import { modifier } from 'ember-modifier';
import { t } from 'ember-intl';
import type LoanModel from 'tio-common/models/loan';
import type { VegaLiteSpec } from 'tio-common/types/chart';

export interface LoanSummaryChartSignature {
  Args: {
    loans: LoanModel[];
  };
}

export default class LoanSummaryChart extends Component<LoanSummaryChartSignature> {
  private chartElement: HTMLElement | null = null;
  private resizeTimeout?: ReturnType<typeof setTimeout>;

  get loans() {
    return this.args.loans;
  }

  get chartData() {
    // Filter out loans with null/zero balance - vega arc charts can't render zero theta values
    return this.loans
      .filter((loan) => loan.currentBalance && loan.currentBalance > 0)
      .map((loan) => ({
        category: loan.name ?? `Loan ${loan.id}`,
        value: loan.currentBalance,
        interestRate: (loan.interestRate ?? 0) / 100, // Convert to decimal for percentage formatting
      }));
  }

  // TrackedFunction to automatically re-render chart when data changes
  chartRendering = trackedFunction(this, async () => {
    if (!this.chartElement || this.chartData.length === 0) return;

    // Get container dimensions for responsive sizing
    const containerRect = this.chartElement.getBoundingClientRect();
    const availableSize = Math.min(containerRect.width || 300, containerRect.height || 300);
    const size = Math.max(Math.min(availableSize - 40, 300), 150);

    // Create custom spec with proper currency formatting
    const spec: VegaLiteSpec = {
      $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
      width: size,
      height: size,
      data: { values: this.chartData },
      mark: {
        type: 'arc' as const,
        tooltip: true,
        innerRadius: 50,
      },
      encoding: {
        theta: { field: 'value', type: 'quantitative' as const },
        color: {
          field: 'category',
          type: 'nominal' as const,
          scale: {
            range: [
              '#10B981', // emerald-500
              '#F59E0B', // amber-500
              '#EF4444', // red-500
              '#8B5CF6', // violet-500
              '#06B6D4', // cyan-500
              '#F97316', // orange-500
              '#84CC16', // lime-500
              '#3B82F6', // blue-500
            ],
          },
          legend: {
            orient: 'right',
            titleLimit: 100,
            labelLimit: 100,
          },
        },
        tooltip: [
          { field: 'category', type: 'nominal' as const, title: 'Loan' },
          {
            field: 'value',
            type: 'quantitative' as const,
            title: 'Balance',
            format: '$,.0f',
          },
          {
            field: 'interestRate',
            type: 'quantitative' as const,
            title: 'Interest Rate',
            format: '.2%',
          },
        ],
      },
    };

    // Use vega-embed to render our custom spec
    try {
      // Clear previous content
      this.chartElement.innerHTML = '';

      // Dynamically import vega-embed for code-splitting
      const { default: vegaEmbed } = await import('vega-embed');
      await vegaEmbed(this.chartElement, spec, {
        actions: false,
        padding: 5,
        renderer: 'svg',
      });
    } catch (error) {
      console.error('Error rendering loan summary chart:', error);
      this.chartElement.innerHTML = `
        <div class="text-red-500 p-4 border border-red-200 rounded">
          <h3 class="font-semibold mb-2">Error rendering chart</h3>
          <p class="text-sm">${
            error instanceof Error ? error.message : 'Unknown error occurred'
          }</p>
        </div>
      `;
    }
  });

  // Modifier to set up the chart element and trigger rendering
  renderChart = modifier((element: HTMLElement) => {
    this.chartElement = element;

    // Set up ResizeObserver to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Debounce resize events to avoid excessive re-renders
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.chartRendering.value; // Re-render chart with new dimensions
      }, 150);
    });

    // Observe the container for size changes
    resizeObserver.observe(element);

    // Trigger initial chart rendering
    this.chartRendering.value;

    return () => {
      resizeObserver.disconnect();
      clearTimeout(this.resizeTimeout);
      this.chartElement = null;
    };
  });

  <template>
    {{#if this.chartData.length}}
      <div
        {{this.renderChart}}
        data-test-loan-summary-chart
        class="h-96 w-full flex justify-center items-center"
      >
        {{! Chart will be rendered here by vega-embed }}
      </div>
    {{else}}
      <div class="h-96 w-full flex justify-center items-center">
        <div class="text-gray-500 text-center">
          <div class="text-sm">{{t "no_chart_data"}}</div>
        </div>
      </div>
    {{/if}}
  </template>
}
